/**
 * 로컬 개발 환경 — 설치 항목·첫 기동 절차·함정.
 * 함정 목록은 실제로 팀에서 시간을 잡아먹었던 것만 남긴다. 새 사람이 같은 데서 막히면 여기에 한 줄 늘린다.
 */
export interface Prereq {
  name: string;
  why: string;
  install: string;
}

export interface SetupStep {
  title: string;
  detail: string;
  commands: string[];
}

export interface Pitfall {
  symptom: string;
  cause: string;
  fix: string;
  /** danger: 그냥 막히는 게 아니라 남의 환경·작업을 깨뜨린다. */
  severity: 'danger' | 'warn';
}

export const PREREQS: Prereq[] = [
  {
    name: 'nvm + Node',
    why: '저장소마다 Node 버전이 다릅니다(`.nvmrc`). 버전이 어긋나면 빌드가 ERR_REQUIRE_ESM 같은 엉뚱한 에러로 죽습니다',
    install: 'brew install nvm && nvm install 24.18.0 && nvm install 22',
  },
  {
    name: 'colima (Docker 런타임)',
    why: '로컬 MySQL·Redis와 testcontainers 기반 e2e가 도커를 씁니다',
    install: 'brew install colima docker docker-compose && colima start',
  },
  {
    name: 'gcloud CLI',
    why: 'Cloud SQL 접속·Secret Manager 조회·로그 확인에 씁니다',
    install: 'brew install --cask google-cloud-sdk && gcloud auth login',
  },
  {
    name: 'cloud-sql-proxy',
    why: 'dev DB에 붙는 유일한 경로입니다. 인스턴스에 직접 접속하지 않습니다',
    install: 'brew install cloud-sql-proxy',
  },
  {
    name: 'gh CLI',
    why: '이슈·PR을 터미널에서 다룹니다. 팀 워크플로가 GitHub Issues 위에 올라가 있습니다',
    install: 'brew install gh && gh auth login',
  },
];

export const FIRST_RUN: SetupStep[] = [
  {
    title: '1. 📦 저장소 받기',
    detail: '워크스페이스 루트에서 한 번에 받습니다. 토큰은 GitHub Settings → Developer settings에서 만들어 주세요',
    commands: ['cd ~/workspace/ndmarket', './clone-missing-repos.sh <GITHUB_TOKEN>'],
  },
  {
    title: '2. 🖥️ 개발자 콘솔 띄우기 (지금 이 화면)',
    detail: '의존성이 가장 적어서 환경이 제대로 잡혔는지 확인하기 좋습니다',
    commands: ['cd nd-developer-console', 'nvm use', 'npm install', 'docker-compose up -d', 'npm run dev:web'],
  },
  {
    title: '3. ⚙️ 메인 API 띄우기',
    detail: '`.env.local`이 필요합니다 — 팀에 요청해 주세요(저장소에는 `.env.example`만 있습니다)',
    commands: ['cd nd-market-api', 'nvm use', 'npm install', 'npm run start:local'],
  },
  {
    title: '4. 🔌 프런트를 로컬 API에 붙이기',
    detail: '`.env.local`의 `VITE_API_URL`을 로컬(3000)로 두면 내 API를, dev 주소로 두면 개발 서버를 봅니다',
    commands: ['cd vue_retail', 'nvm use', 'npm install', 'npm run dev'],
  },
];

export const PITFALLS: Pitfall[] = [
  {
    severity: 'danger',
    symptom: '3306 / 3307 포트를 로컬 컨테이너에 매핑',
    cause: '두 포트는 cloud-sql-proxy 전용입니다. 다른 프로세스가 잡으면 팀원의 DB 접속이 조용히 엉뚱한 곳으로 갑니다',
    fix: '로컬 MySQL은 다른 포트로 띄워 주세요(개발자 콘솔은 3316). testcontainers는 고정 매핑 없이 랜덤 포트를 씁니다',
  },
  {
    severity: 'danger',
    symptom: 'vue_retail에서 `npm run lint`을 무심코 실행',
    cause: '이 저장소의 lint 스크립트에는 `--fix`가 붙어 있어 작업 트리를 통째로 고쳐 씁니다',
    fix: '남의 변경이 섞인 상태에서는 돌리지 말아 주세요. 확인만 하려면 `npx eslint <경로>`로 파일을 좁혀 봅니다',
  },
  {
    severity: 'warn',
    symptom: 'nd-market-api에서 `npm install`이 @sentry/profiling-node 빌드로 실패',
    cause: 'Node 24 + macOS + 해당 패키지의 네이티브 빌드 조합 문제입니다',
    fix: '`npm install --ignore-scripts` → 해당 패키지의 `binding.gyp` 삭제 → `npm rebuild` 순서로 우회합니다',
  },
  {
    severity: 'warn',
    symptom: '다른 저장소로 옮긴 뒤 빌드가 갑자기 깨짐',
    cause: '`.nvmrc`가 저장소마다 다른데 셸의 Node가 그대로입니다',
    fix: '디렉토리를 옮길 때마다 `nvm use`를 실행해 주세요. 자동화하려면 셸 훅을 걸면 됩니다',
  },
  {
    severity: 'warn',
    symptom: 'testcontainers e2e가 "Docker 미가용"으로 죽음',
    cause: 'colima 소켓 경로가 기본 도커 소켓과 달라 라이브러리가 찾지 못합니다',
    fix: 'colima 소켓을 `DOCKER_HOST`로 넘기고 `TESTCONTAINERS_RYUK_DISABLED=true`로 실행합니다',
  },
  {
    severity: 'warn',
    symptom: '개발자 콘솔 4700이 무슨 경로를 쳐도 200을 줌',
    cause: 'vite dev 서버는 모르는 경로에도 `index.html`을 돌려줍니다 — 헬스체크가 아닙니다',
    fix: '떴는지는 브라우저로 확인해 주세요. API가 살아 있는지는 4701의 `/healthz`를 보면 됩니다',
  },
];
